import type { Channel, ChannelGroup } from '@shared/types.ts';
import type { ChannelFilter } from '@/lib/queries.ts';
import { useSetChannelPref } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';
import { useLiveView } from '@/stores/viewMode.ts';

/**
 * Everything the MENU key offers on the channel grid: what to do with the
 * channel under the cursor, and what the grid should be showing at all.
 *
 * WHY FILTERING LIVES HERE AND NOT IN A ROW ON SCREEN.
 *
 * It used to be a rail of chips above the grid, which is the right answer for
 * a lineup of four hundred channels and the wrong one for fifty. At fifty it
 * is a permanent row of clutter you scroll past, and — worse on a remote — a
 * whole extra focus lane between the toolbar and the content, which is what
 * made the D-pad feel like it was wandering. A set-top box puts lineup
 * options behind the context key for exactly this reason: the screen shows
 * the channels, and the key you already press to act on one is where the rest
 * of the options live.
 *
 * The cost is discoverability — nothing on screen says filtering exists — so
 * the grid shows the active filter as a caption whenever it is not "All", and
 * that caption names MENU.
 *
 * SPELLING: everything the viewer reads says "favorites"; the wire field, the
 * query parameter and the SQLite column stay `favourite`. That split is
 * deliberate. Renaming the data would mean a wire contract change, a database
 * migration and a pass over the tests, all to settle an orthography question
 * — and half-renaming it is how you end up with a filter that silently
 * matches nothing. Display strings are the layer where language belongs.
 *
 * `channel` is null when MENU was pressed with nothing focused on a card: the
 * per-channel half disappears and the filter half still works, rather than
 * the key doing nothing and reading as broken.
 *
 * <Modal> owns the parts a dialog on a television has to get right: it takes
 * the remote on open, confines the arrow keys to itself, hands focus back on
 * close, and makes BACK close the menu rather than leave the screen. Focus
 * lands on the first [data-nav] inside, which is why favourites is first — it
 * is the item people come here for.
 */
export function ChannelMenu({
  channel, groups = [], favourites = 0, hidden = 0, filter = {}, onFilter, onClose,
}: {
  channel: Channel | null;
  groups?: ChannelGroup[];
  favourites?: number;
  hidden?: number;
  filter?: ChannelFilter;
  /**
   * Absent from the guide, which has no lineup filter of its own. The whole
   * Show section disappears with it rather than rendering options that do
   * nothing — a menu item that is present and inert is worse than one that is
   * not there.
   */
  onFilter?(next: ChannelFilter): void;
  onClose(): void;
}) {
  const { view, set: setView } = useLiveView();
  const setPref = useSetChannelPref();

  const toggle = (patch: { favourite?: boolean; hidden?: boolean }) => {
    if (!channel) return;
    setPref.mutate({ id: channel.id, patch });
    onClose();
  };

  const choose = (next: ChannelFilter) => { onFilter?.(next); onClose(); };

  const chooseView = (next: 'guide' | 'tiles') => { setView(next); onClose(); };

  const isAll = !filter.group && !filter.favourites && !filter.hidden;

  return (
    <Modal className="menu" onClose={onClose} labelledBy="menu-title">
      {channel && (
        <>
          <div className="menu-head">
            <div className="menu-logo"><ChannelLogo channel={channel} className="logo-inner" /></div>
            <div>
              <div className="big" id="menu-title">{channel.name}</div>
              <div className="muted">
                Ch. {channel.number}
                {channel.categories[0] ? ` · ${channel.categories[0]}` : ''}
              </div>
            </div>
          </div>

          <div className="menu-items">
            <Focusable
              id="menu-fav"
              className="btn menu-item"
              onSelect={() => toggle({ favourite: !channel.favourite })}
            >
              {channel.favourite ? '★  Remove from favorites' : '☆  Add to favorites'}
            </Focusable>

            <Focusable
              id="menu-hide"
              className="btn menu-item"
              onSelect={() => toggle({ hidden: !channel.hidden })}
            >
              {channel.hidden ? 'Show this channel' : 'Hide this channel'}
            </Focusable>
          </div>
        </>
      )}

      {/*
        HOW to show Live TV, which used to be two entries in the navigation.
        It belongs with the other things you do to the screen you are on, and
        this is where people already come looking.
      */}
      <div className="menu-section" id={channel ? undefined : 'menu-title'}>View</div>

      <div className="menu-items">
        <Focusable
          id="view-guide"
          className={`btn menu-item ${view === 'guide' ? 'active' : ''}`}
          onSelect={() => chooseView('guide')}
        >
          Guide
        </Focusable>
        <Focusable
          id="view-tiles"
          className={`btn menu-item ${view === 'tiles' ? 'active' : ''}`}
          onSelect={() => chooseView('tiles')}
        >
          Channel tiles
        </Focusable>
      </div>

      {onFilter && (
      <>
      <div className="menu-section">Show</div>

      {/* The filter list is the one part that can get long — a provider can
          name twenty categories — so it scrolls on its own rather than pushing
          the actions it sits under off the screen. */}
      <div className="menu-items menu-scroll">
        <Focusable
          id="show-all"
          className={`btn menu-item ${isAll ? 'active' : ''}`}
          onSelect={() => choose({})}
        >
          All channels
        </Focusable>

        <Focusable
          id="show-favourites"
          className={`btn menu-item ${filter.favourites ? 'active' : ''}`}
          onSelect={() => choose({ favourites: true })}
        >
          ★  Favorites <span className="menu-count">{favourites}</span>
        </Focusable>

        {/* Only when there is something to get back. An entry that is always
            there and always empty teaches people to ignore it. */}
        {hidden > 0 && (
          <Focusable
            id="show-hidden"
            className={`btn menu-item ${filter.hidden ? 'active' : ''}`}
            onSelect={() => choose({ hidden: true })}
          >
            Hidden channels <span className="menu-count">{hidden}</span>
          </Focusable>
        )}

        {groups.map((g) => (
          <Focusable
            key={g.name}
            id={`show-${g.name}`}
            className={`btn menu-item ${filter.group === g.name ? 'active' : ''}`}
            onSelect={() => choose({ group: g.name })}
          >
            {g.name} <span className="menu-count">{g.count}</span>
          </Focusable>
        ))}
      </div>
      </>
      )}

      <Focusable id="menu-close" className="btn menu-item menu-cancel" onSelect={onClose}>
        Cancel
      </Focusable>
    </Modal>
  );
}
